// Facility-staff RBAC helpers. Users WITHOUT a granular permissions dict
// (grandfathered facility admins created before the invite system) get full access.
const permDict = (user) => {
    const p = user?.permissions;
    return p && typeof p === "object" && !Array.isArray(p) ? p : null;
};

export const hasPerm = (user, perm) => {
    if (!user) return false;
    if (user.role === "admin") return true;
    const p = permDict(user);
    if (!p) return true;
    return !!p[perm];
};

// True if the user has ANY of the listed permissions (used for functions accessible
// to either custody OR clinical staff).
export const hasAnyPerm = (user, perms = []) => perms.some((p) => hasPerm(user, p));

// Deceased roster access is gated to supervisors + medical leadership (Manage Staff,
// Supervisor Reports, Mortality Review, or Care Management perms) plus the facility
// owner/platform admin — mirrors the backend gate in routes/institution/inmates.py.
export const canViewDeceased = (user) => {
    if (!user) return false;
    if (user.role === "admin") return true;
    if (!user.invited_by) return true;
    const p = permDict(user);
    if (!p) return true;
    return ["manage_staff", "supervisor_reports", "mortality_review", "care_management"].some((k) => !!p[k]);
};

// True if the inmate's chart is archived/read-only per chart_lock.py on the backend
// (deceased, released, or transferred). Mirrors LOCKED_STATUSES there.
export const isChartLocked = (inmate) => ["deceased", "released", "transferred"].includes(inmate?.status);

export const hasChart = (user, feature) => {
    if (!user) return false;
    if (user.role === "admin") return true;
    const p = permDict(user);
    if (!p) return true;
    return (p.chart_access || []).includes(feature);
};

// Supervisor authority (mirrors backend routes.institution._common.is_supervisor):
// platform admin, facility owner, manage_staff / housing_override, or a supervisor /
// unit-manager facility role. Gates the full record + mortality packet downloads.
export function isSupervisorUser(user) {
    if (!user) return false;
    if (user.role === "admin") return true;
    if (["supervisor", "unit_manager"].includes(user.facility_role)) return true;
    const p = user.permissions;
    if (!p || typeof p !== "object") return ["institution", "institution_admin"].includes(user.role) && !user.invited_by;
    return !!p.manage_staff;
}

// Staff names go on legal paperwork — first AND last name, not "Tera K".
export function isFullName(v) {
    const parts = String(v || "").trim().split(/\s+/).filter((w) => w.replace(/[^A-Za-z]/g, "").length >= 2);
    return parts.length >= 2;
}
